/*
 * Общие элементы карточек хранилищ: заголовок с иконкой, состояние загрузки/ошибки,
 * таблица без обёрток material-table, подписи-факты в шапке.
 */
import { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  EmptyState,
  InfoCard,
  Progress,
  ResponseErrorPanel,
  StatusError,
  StatusOK,
  StatusPending,
  StatusWarning,
} from '@backstage/core-components';
import { AsyncState } from 'react-use/esm/useAsync';
import { NOT_CONNECTED } from './useDatastoreData';

export const useDsStyles = makeStyles(theme => ({
  title: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
  },
  titleIcon: {
    width: 34,
    height: 34,
    borderRadius: 12,
    background: theme.palette.action.selected,
    color: theme.palette.primary.main,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  facts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 2.5),
    marginBottom: theme.spacing(1.5),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  factValue: { color: theme.palette.text.primary, fontWeight: 700 },
  mono: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5 },
  muted: { color: theme.palette.text.secondary },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 22,
    padding: '0 9px',
    borderRadius: 999,
    background: theme.palette.action.selected,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    marginRight: 6,
  },
  nowrap: { whiteSpace: 'nowrap' },
  scroll: { overflowX: 'auto' },
  foot: {
    marginTop: theme.spacing(1.5),
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
}));

/** Русское множественное: plural(3, 'узел', 'узла', 'узлов'). */
export const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

/** Индикатор готовности: undefined — нет данных. */
export const ReadyStatus = ({ ready }: { ready?: boolean }) => {
  const classes = useDsStyles();
  if (ready === undefined) return <span className={classes.muted}>—</span>;
  return ready ? (
    <StatusOK>Ready</StatusOK>
  ) : (
    <StatusWarning>Not ready</StatusWarning>
  );
};

export const CardTitle = ({
  icon,
  text,
}: {
  icon: ReactNode;
  text: string;
}) => {
  const classes = useDsStyles();
  return (
    <span className={classes.title}>
      <span className={classes.titleIcon}>{icon}</span>
      {text}
    </span>
  );
};

export const Fact = ({ label, value }: { label: string; value: ReactNode }) => {
  const classes = useDsStyles();
  return (
    <span>
      {label} <span className={classes.factValue}>{value}</span>
    </span>
  );
};

/** Обёртка карточки: загрузка, ошибка, «кластер не подключён», затем содержимое. */
export function DatastoreCard<T extends { available: boolean }>({
  title,
  subheader,
  state,
  errorTitle,
  children,
}: {
  title: ReactNode;
  subheader?: (data: T) => string | undefined;
  state: AsyncState<T>;
  errorTitle: string;
  children: (data: T) => ReactNode;
}) {
  if (state.loading) {
    return (
      <InfoCard title={title}>
        <Progress />
      </InfoCard>
    );
  }
  if (state.error) {
    return <ResponseErrorPanel title={errorTitle} error={state.error} />;
  }
  if (!state.value?.available) {
    return (
      <InfoCard title={title}>
        <EmptyState
          missing="info"
          title="Кластер хранилища не подключён к плагину Kubernetes"
          description={NOT_CONNECTED}
        />
      </InfoCard>
    );
  }
  return (
    <InfoCard title={title} subheader={subheader?.(state.value)}>
      {children(state.value)}
    </InfoCard>
  );
}

export const PodStatus = ({
  ready,
  phase,
}: {
  ready: boolean;
  phase?: string;
}) => {
  if (ready) return <StatusOK>Running</StatusOK>;
  if (phase === 'Pending') return <StatusPending>Pending</StatusPending>;
  if (phase === 'Running')
    return <StatusWarning>Running · not ready</StatusWarning>;
  return <StatusError>{phase ?? 'Unknown'}</StatusError>;
};

export const DsTable = ({
  head,
  children,
  empty,
}: {
  head: string[];
  children: ReactNode;
  empty?: string;
}) => {
  const classes = useDsStyles();
  return (
    <div className={classes.scroll}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {head.map(h => (
              <TableCell key={h}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {children}
          {empty && (
            <TableRow>
              <TableCell colSpan={head.length}>
                <Typography variant="body2" className={classes.muted}>
                  {empty}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
